import React from 'react';
import { Modal, View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useThemeColors } from '../constants/theme';

/**
 * AVISO DESTACADO DE UBICACIÓN EN SEGUNDO PLANO (Prominent Disclosure)
 * ---------------------------------------------------------------------------
 * REQUISITO OBLIGATORIO de Google Play. La app fue RECHAZADA (2026-07) porque:
 *   - No existía este aviso, y
 *   - El video de declaración debe mostrar "cómo los usuarios activan el aviso
 *     destacado relativo a la ubicación en segundo plano".
 *
 * Reglas que cumple este componente (política de Google Play — Ubicación):
 *   1) Aparece ANTES del diálogo de permiso del sistema.
 *   2) Dice explícitamente que se recopila la ubicación EN SEGUNDO PLANO
 *      (cuando la app está cerrada o minimizada).
 *   3) Explica PARA QUÉ se usa (finalidad concreta, no genérica).
 *   4) Exige una ACCIÓN AFIRMATIVA del usuario ("Continuar") — no es un aviso
 *      pasivo ni se puede saltar tocando fuera.
 *   5) Ofrece rechazar ("Ahora no") sin castigo.
 *
 * NO borrar ni ocultar sin revisar la política: es causa de rechazo/retiro.
 */

interface Props {
  visible: boolean;
  /** El usuario aceptó → el caller debe pedir el permiso del sistema. */
  onAccept: () => void;
  /** El usuario rechazó → el caller NO debe pedir el permiso. */
  onDecline: () => void;
}

export default function BackgroundLocationDisclosure({ visible, onAccept, onDecline }: Props) {
  const colors = useThemeColors();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      // onRequestClose = botón atrás de Android → equivale a rechazar.
      onRequestClose={onDecline}
    >
      <View style={styles.backdrop}>
        <View style={[styles.card, { backgroundColor: colors.card }]}>
          <View style={styles.iconCircle}>
            <Ionicons name="navigate-circle" size={38} color="#FFF" />
          </View>

          <Text style={[styles.title, { color: colors.text }]}>
            Go Safely usa tu ubicación en segundo plano
          </Text>

          <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={false}>
            <Text style={[styles.body, { color: colors.textSecondary }]}>
              Para tu seguridad y la del pasajero, Go Safely recopila tu ubicación{' '}
              <Text style={[styles.bold, { color: colors.text }]}>
                incluso cuando la app está cerrada o en segundo plano
              </Text>
              , con estos fines:
            </Text>

            <View style={styles.bullet}>
              <Ionicons name="shield-checkmark" size={18} color={colors.primary} />
              <Text style={[styles.bulletText, { color: colors.textSecondary }]}>
                Mostrar tu viaje en tiempo real al pasajero y a sus contactos de emergencia.
              </Text>
            </View>
            <View style={styles.bullet}>
              <Ionicons name="pulse" size={18} color={colors.primary} />
              <Text style={[styles.bulletText, { color: colors.textSecondary }]}>
                Mantener activa la señal del viaje aunque minimices la app o se apague la pantalla.
              </Text>
            </View>
            <View style={styles.bullet}>
              <Ionicons name="car-sport" size={18} color={colors.primary} />
              <Text style={[styles.bulletText, { color: colors.textSecondary }]}>
                Asignarte los viajes y entregas más cercanos mientras estás en línea.
              </Text>
            </View>

            <Text style={[styles.note, { color: colors.textSecondary }]}>
              Solo se recopila mientras estás{' '}
              <Text style={[styles.bold, { color: colors.text }]}>EN LÍNEA</Text> o en un viaje
              activo. Puedes detenerlo en cualquier momento poniéndote fuera de línea, o revocando
              el permiso desde los ajustes de tu teléfono.
            </Text>
          </ScrollView>

          <TouchableOpacity style={styles.acceptBtn} onPress={onAccept} activeOpacity={0.85}>
            <Text style={styles.acceptText}>Continuar</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.declineBtn} onPress={onDecline} activeOpacity={0.7}>
            <Text style={[styles.declineText, { color: colors.textSecondary }]}>Ahora no</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 22,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 20,
    padding: 22,
    alignItems: 'center',
  },
  iconCircle: {
    width: 68,
    height: 68,
    borderRadius: 34,
    backgroundColor: '#208AEF',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  title: { fontSize: 19, fontWeight: '800', textAlign: 'center', marginBottom: 12 },
  body: { fontSize: 14, lineHeight: 20, marginBottom: 14 },
  bold: { fontWeight: '800' },
  bullet: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginBottom: 10 },
  bulletText: { flex: 1, fontSize: 13.5, lineHeight: 19 },
  note: { fontSize: 12.5, lineHeight: 18, marginTop: 6, fontStyle: 'italic' },
  acceptBtn: {
    backgroundColor: '#208AEF',
    borderRadius: 14,
    paddingVertical: 15,
    width: '100%',
    alignItems: 'center',
    marginTop: 18,
  },
  acceptText: { color: '#FFF', fontWeight: '800', fontSize: 16 },
  declineBtn: { paddingVertical: 12, width: '100%', alignItems: 'center' },
  declineText: { fontSize: 14, fontWeight: '600' },
});
